// A27 — Dasturchi vositalari. Module-local data layer. The API console (A27.01)
// hits REAL admin endpoints via the shared client (functional now); cache/health
// use `/admin/devtools/*` + `/health` (fail-soft). Does NOT touch HOT files.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ConsoleResult {
  ok: boolean
  data: unknown
}

/** A27.01 — run an arbitrary admin API request and return the parsed result. */
export async function runRequest(
  method: HttpMethod,
  path: string,
  body?: string,
): Promise<ConsoleResult> {
  let parsed: unknown
  if (body && body.trim()) {
    try {
      parsed = JSON.parse(body)
    } catch {
      return { ok: false, data: 'Invalid JSON body' }
    }
  }
  try {
    let data: unknown
    switch (method) {
      case 'POST':
        data = await api.post(path, parsed)
        break
      case 'PUT':
        data = await api.put(path, parsed)
        break
      case 'PATCH':
        data = await api.patch(path, parsed)
        break
      case 'DELETE':
        data = await api.del(path)
        break
      default:
        data = await api.get(path)
    }
    return { ok: true, data }
  } catch (e) {
    return { ok: false, data: e instanceof Error ? e.message : String(e) }
  }
}

/** A27.14 — health probe (real `/health`; fail-soft to an error object). */
export async function fetchHealth(signal?: AbortSignal): Promise<ConsoleResult> {
  try {
    const data = await api.get('/health', signal ? { signal } : undefined)
    return { ok: true, data }
  } catch (e) {
    return { ok: false, data: e instanceof Error ? e.message : String(e) }
  }
}

/** A27.11 — clear a named cache (dormant `/admin/devtools/cache/clear`). */
export function clearCache(scope: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/devtools/cache/clear', { scope })
}

/** A27.08 — a seeded test user. */
export interface TestUserRow {
  id: number
  name: string
  phone: string
  created_at: string
}

/** A27.10 — a DB migration + whether it is applied. */
export interface MigrationRow {
  version: string
  name: string
  applied: boolean
  applied_at: string | null
}

export function fetchTestUsers(params: FetchParams): Promise<FetchResult<TestUserRow>> {
  return safeList<TestUserRow>('/admin/devtools/test-users', 'users', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.phone}`),
  )
}

/** POST /admin/devtools/test-users — seed one (A27.08). */
export function createTestUser(): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/devtools/test-users', {})
}

/** DELETE /admin/devtools/test-users/:id (A27.08). */
export function deleteTestUser(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/devtools/test-users/${id}`)
}

export function fetchMigrations(params: FetchParams): Promise<FetchResult<MigrationRow>> {
  return safeList<MigrationRow>('/admin/devtools/migrations', 'migrations', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.version} ${r.name}`),
  )
}

/** POST /admin/devtools/restart {service} — graceful restart (A27.12). */
export function restartService(service: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/devtools/restart', { service })
}

/* ─────────────────────────────────────────────────────────────────────
 * A27.02–A27.13 — greenfield dev-tools. Fetchers fail-soft to empty
 * (safeList/try-catch) so screens render clean until the endpoints ship.
 * A27.06 attempts the real `/admin/job-runs?status=error`; A27.13 the real
 * `/admin/config/history` — both degrade to empty when absent. The live-event
 * stream (A27.05, real `/admin/devtools/events`) and the environment panel
 * (A27.07, real `/admin/devtools/env`) are FUNCTIONAL — no event bus needed;
 * they read the existing `updates`/`admin_audit` tables and live host status.
 * ───────────────────────────────────────────────────────────────────── */

/** A27.02 — a recent admin API call captured by the request inspector. */
export interface RequestLogRow {
  id: number
  at: string
  method: string
  path: string
  status: number
  duration_ms: number
  actor: string
}

export function fetchRequestLog(params: FetchParams): Promise<FetchResult<RequestLogRow>> {
  return safeList<RequestLogRow>('/admin/devtools/requests', 'requests', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.method} ${r.path} ${r.actor}`),
  )
}

/** A27.03 — one webhook delivery attempt + its outcome. */
export interface WebhookDeliveryRow {
  id: number
  at: string
  event: string
  url: string
  status_code: number
  attempt: number
  duration_ms: number
  ok: boolean
}

export function fetchWebhookDeliveries(
  params: FetchParams,
): Promise<FetchResult<WebhookDeliveryRow>> {
  return safeList<WebhookDeliveryRow>(
    '/admin/devtools/webhook-deliveries',
    'deliveries',
    params.signal,
  ).then((rows) => toResult(rows, params, (r) => `${r.event} ${r.url}`))
}

/** POST /admin/devtools/webhook-deliveries/:id/resend — replay one delivery (A27.03). */
export function resendWebhook(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/devtools/webhook-deliveries/${id}/resend`, {})
}

/** A27.04 — a raw Telegram-style Update object seen by the platform. */
export interface UpdateRow {
  id: number
  update_id: number
  type: string
  chat: string
  received_at: string
  payload: unknown
}

export function fetchUpdates(params: FetchParams): Promise<FetchResult<UpdateRow>> {
  return safeList<UpdateRow>('/admin/devtools/updates', 'updates', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.type} ${r.chat}`),
  )
}

/**
 * A27.05 — a single row from the live activity stream. Merged from the real
 * `updates` (source `update`) + `admin_audit` (source `audit`) tables; the id is
 * a prefixed string ("u123" / "a456") so the two sources never collide.
 */
export interface EventRow {
  id: string
  at: string
  type: string
  source: string
  payload: unknown
}

/** A27.05 — recent activity feed (real `/admin/devtools/events`, newest first). */
export function fetchEventStream(params: FetchParams): Promise<FetchResult<EventRow>> {
  return safeList<EventRow>('/admin/devtools/events?limit=100', 'events', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.type} ${r.source}`),
  )
}

/** A27.06 — an error trace / failed job run. */
export interface ErrorTraceRow {
  id: number
  at: string
  level: string
  service: string
  message: string
  trace_id: string
}

/**
 * A27.06 — wired to the real `/admin/job-runs?status=error` (falls back to
 * `traces` key); fail-soft to empty until that endpoint ships.
 */
export async function fetchErrorTraces(params: FetchParams): Promise<FetchResult<ErrorTraceRow>> {
  let rows: ErrorTraceRow[] = []
  try {
    const data = await api.get<Record<string, ErrorTraceRow[]>>('/admin/job-runs', {
      signal: params.signal,
      query: { status: 'error' },
    })
    rows =
      (data?.runs as ErrorTraceRow[] | undefined) ??
      (data?.traces as ErrorTraceRow[] | undefined) ??
      []
  } catch {
    rows = []
  }
  return toResult(rows, params, (r) => `${r.service} ${r.message} ${r.trace_id}`)
}

/** A27.07 — one known feature flag (feature_* key) + its stored value. */
export interface EnvFlag {
  name: string
  value: string
}

/** A27.07 — real read-only environment info reported by the backend. */
export interface EnvInfo {
  env: string
  version: string
  go_version: string
  postgres: boolean
  redis: boolean
  restart_enabled: boolean
  feature_flags: EnvFlag[]
}

/** A27.07 — environment panel (real `/admin/devtools/env`). Fail-soft. */
export async function fetchEnv(
  signal?: AbortSignal,
): Promise<{ ok: boolean; data: EnvInfo | null; error?: string }> {
  try {
    const data = await api.get<EnvInfo>('/admin/devtools/env', signal ? { signal } : undefined)
    return { ok: true, data }
  } catch (e) {
    return { ok: false, data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** A27.09 — result of a test-data generation run. */
export interface GenerateResult {
  created: number
}

/** POST /admin/devtools/generate {entity, count} — seed fake records (A27.09). */
export function generateTestData(entity: string, count: number): Promise<GenerateResult> {
  return api.post<GenerateResult>('/admin/devtools/generate', { entity, count })
}

/** A27.13 — one config change (old→new) for the diff viewer. */
export interface ConfigChangeRow {
  id: number
  key: string
  old_value: string
  new_value: string
  changed_by: string
  changed_at: string
}

/** A27.13 — attempts the real `/admin/config/history`; fail-soft to empty. */
export function fetchConfigHistory(params: FetchParams): Promise<FetchResult<ConfigChangeRow>> {
  return safeList<ConfigChangeRow>('/admin/config/history', 'history', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.key} ${r.changed_by}`),
  )
}

export const DT_STR = {
  console: 'API-консоль',
  cache: 'Очистка кэша',
  health: 'Проверка здоровья',
  method: 'Метод',
  path: 'Путь',
  body: 'Тело (JSON)',
  send: 'Отправить',
  response: 'Ответ',
  running: 'Выполняется…',
  clear: 'Очистить',
  refresh: 'Обновить',
  scopeAll: 'Весь кэш',
  scopeUsers: 'Кэш пользователей',
  scopeConfig: 'Кэш конфигурации',
  cleared: 'Кэш очищен',
  failed: 'Ошибка',
  ok: 'OK',
  // A27.08 test users
  testUsers: 'Тестовые пользователи',
  name: 'Имя',
  phone: 'Телефон',
  created: 'Создан',
  seed: 'Создать тестового',
  delete: 'Удалить',
  seeded: 'Тестовый пользователь создан',
  deleted: 'Удалён',
  // A27.10 migrations
  migrations: 'Миграции БД',
  version: 'Версия',
  applied: 'Применена',
  appliedAt: 'Когда',
  // A27.12 restart
  restart: 'Перезапуск сервиса',
  restarted: 'Перезапуск инициирован',
  svcApi: 'API',
  svcGateway: 'Gateway',
  svcWorker: 'Worker',
  // A27.02 request inspector
  requestInspector: 'Инспектор запросов',
  at: 'Время',
  status: 'Статус',
  duration: 'Длит., мс',
  actor: 'Кто',
  // A27.03 webhook debug
  webhookDebug: 'Отладка webhook',
  eventCol: 'Событие',
  url: 'URL',
  statusCode: 'Код',
  attempt: 'Попытка',
  resend: 'Повторить',
  resent: 'Повторная доставка запущена',
  // A27.04 update viewer
  updateViewer: 'Просмотр апдейтов',
  updateId: 'update_id',
  type: 'Тип',
  chat: 'Чат',
  received: 'Получен',
  payload: 'Данные',
  // A27.05 event stream
  eventStream: 'Поток событий (live)',
  source: 'Источник',
  pause: 'Пауза',
  resume: 'Возобновить',
  livePolling: 'Обновление каждые 5с',
  // A27.06 error trace
  errorTrace: 'Трассировка ошибок',
  level: 'Уровень',
  service: 'Сервис',
  message: 'Сообщение',
  traceId: 'trace_id',
  // A27.07 sandbox / environment panel
  sandbox: 'Песочница',
  sandboxInfo: 'Параметры окружения',
  apiBase: 'API base',
  envMode: 'Режим',
  buildVersion: 'Версия сборки',
  goVersion: 'Go',
  postgres: 'PostgreSQL',
  redis: 'Redis',
  restartGate: 'Перезапуск разрешён',
  featureFlags: 'Feature-флаги',
  online: 'Онлайн',
  offline: 'Офлайн',
  clientEnv: 'Клиент',
  noFlags: 'Флаги не заданы',
  yes: 'Да',
  no: 'Нет',
  backendEnv: 'Сервер',
  // A27.09 data generator
  dataGenerator: 'Генератор данных',
  entity: 'Сущность',
  count: 'Количество',
  generate: 'Сгенерировать',
  generating: 'Генерация…',
  generated: 'Данные созданы',
  entUsers: 'Пользователи',
  entChats: 'Чаты',
  entMessages: 'Сообщения',
  entBots: 'Боты',
  result: 'Результат',
  // A27.13 config diff
  configDiff: 'Diff конфигурации',
  key: 'Ключ',
  oldValue: 'Было',
  newValue: 'Стало',
  changedBy: 'Кем',
  changedAt: 'Когда',
} as const
